

import { useState } from "react";
import type { ChangeEvent } from "react";
import { DataSummary } from "./types";

interface DataManagementSectionProps {
  dataSummary: DataSummary | null;
  loadingDataSummary?: boolean;
  onResetData: (type: 'all' | 'operational') => void;
  onGenerateDummyData: () => void;
  onImportData: (file: File, strategy: 'replace' | 'merge' | 'skip') => void;
  resettingData?: boolean;
  generatingDummyData?: boolean;
  importingData?: boolean;
  readOnly?: boolean;
}

const DataManagementSection: React.FC<DataManagementSectionProps> = ({
  dataSummary,
  loadingDataSummary = false,
  onResetData,
  onGenerateDummyData,
  onImportData,
  resettingData = false,
  generatingDummyData = false,
  importingData = false,
  readOnly = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showOperationalResetConfirm, setShowOperationalResetConfirm] = useState(false);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importStrategy, setImportStrategy] = useState<'replace' | 'merge' | 'skip'>('skip');

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = () => {
    if (!selectedFile) return;
    onImportData(selectedFile, importStrategy);
  };

  return (
    <div className="bg-white rounded-xl border border-stone-100 shadow-sm p-4">
      <h2 className="text-base font-bold text-stone-800 mb-4 font-serif">Data Management</h2>
      
      {/* Data Summary */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-stone-800 mb-3">Data Summary</h3>
        {loadingDataSummary ? (
          <div className="text-center py-8">
            <svg className="animate-spin w-8 h-8 text-amber-500 mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sm text-stone-500 mt-2">Loading data summary...</p>
          </div>
        ) : dataSummary ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-blue-600">{dataSummary.core_entities.venues}</div>
              <div className="text-xs text-blue-700">Venues</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-emerald-600">{dataSummary.core_entities.vendors}</div>
              <div className="text-xs text-emerald-700">Vendors</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-purple-600">{dataSummary.core_entities.services}</div>
              <div className="text-xs text-purple-700">Services</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-amber-600">{dataSummary.core_entities.packages}</div>
              <div className="text-xs text-amber-700">Packages</div>
            </div>
            <div className="bg-rose-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-rose-600">{dataSummary.operational_data.clients}</div>
              <div className="text-xs text-rose-700">Clients</div>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-indigo-600">{dataSummary.operational_data.events}</div>
              <div className="text-xs text-indigo-700">Events</div>
            </div>
            <div className="bg-cyan-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-cyan-600">{dataSummary.operational_data.enquiries}</div>
              <div className="text-xs text-cyan-700">Enquiries</div>
            </div>
            <div className="bg-stone-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-stone-600">{dataSummary.users.total_users}</div>
              <div className="text-xs text-stone-700">Users</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-stone-500">
            <svg className="w-12 h-12 text-stone-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-sm">No data available</p>
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-stone-800">Data Operations</h3>
        
        {/* Import Data */}
        <div className="border border-stone-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium text-stone-800">Import Data</h4>
              <p className="text-xs text-stone-500">Import data from a backup file</p>
            </div>
            <label className="px-3 py-1.5 bg-stone-100 text-stone-700 font-medium rounded-lg hover:bg-stone-200 transition-colors cursor-pointer text-sm">
              Choose File
              <input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                disabled={readOnly}
                className="hidden"
              />
            </label>
          </div>
          
          {selectedFile && (
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-stone-50 rounded-lg p-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm text-stone-700">{selectedFile.name}</span>
                  <span className="text-xs text-stone-500">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-stone-400 hover:text-stone-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">Import Strategy</label>
                <select
                  value={importStrategy}
                  onChange={(e) => setImportStrategy(e.target.value as 'replace' | 'merge' | 'skip')}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                >
                  <option value="skip">Skip Existing (Safe) - Skip records that already exist</option>
                  <option value="merge">Merge - Update existing records, add new ones</option>
                  <option value="replace">Replace All (Dangerous) - Clear all data and import</option>
                </select>
              </div>
              
              <button
                onClick={() => setShowImportConfirm(true)}
                disabled={importingData || readOnly}
                className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {importingData ? "Importing..." : readOnly ? "View Only" : "Import Data"}
              </button>
            </div>
          )}
        </div>

        {/* Reset Operational Data */}
        <div className="border border-orange-200 rounded-xl p-4 bg-orange-50">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-orange-800">Reset Events & Customers</h4>
              <p className="text-xs text-orange-600">Delete all events, enquiries, and client data only (keeps venues & vendors)</p>
            </div>
            <button
              onClick={() => setShowOperationalResetConfirm(true)}
              disabled={resettingData || readOnly}
              className="px-3 py-1.5 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {resettingData ? "Resetting..." : readOnly ? "View Only" : "Reset Events"}
            </button>
          </div>
        </div>

        {/* Generate Dummy Data */}
        <div className="border border-amber-200 rounded-xl p-4 bg-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-stone-800">Generate Sample Data</h4>
              <p className="text-xs text-stone-500">Create dummy data for testing purposes</p>
            </div>
            <button
              onClick={() => setShowGenerateConfirm(true)}
              disabled={generatingDummyData || readOnly}
              className="px-3 py-1.5 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {generatingDummyData ? "Generating..." : readOnly ? "View Only" : "Generate Data"}
            </button>
          </div>
        </div>

        {/* Reset All Data */}
        <div className="border border-red-200 rounded-xl p-4 bg-red-50">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-red-800">Reset All Data</h4>
              <p className="text-xs text-red-600">Permanently delete all data - this cannot be undone</p>
            </div>
            <button
              onClick={() => setShowResetConfirm(true)}
              disabled={resettingData || readOnly}
              className="px-3 py-1.5 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {resettingData ? "Resetting..." : readOnly ? "View Only" : "Reset Data"}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modals */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-stone-800">Reset All Data?</h3>
            </div>
            <p className="text-stone-600 mb-6">
              This will permanently delete all venues, vendors, services, packages, clients, events, and other data. 
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-4 py-2 rounded-xl border border-stone-200 text-stone-600 font-medium hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onResetData('all');
                  setShowResetConfirm(false);
                }}
                className="flex-1 px-4 py-2 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-colors"
              >
                Reset All Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Operational Reset Confirmation */}
      {showOperationalResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-stone-800">Reset Events & Clients?</h3>
            </div>
            <p className="text-stone-600 mb-6">
              This will permanently delete all events, enquiries, and client information. 
              <strong> Venues, Vendors, Services, and Packages will be preserved.</strong>
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowOperationalResetConfirm(false)}
                className="flex-1 px-4 py-2 rounded-xl border border-stone-200 text-stone-600 font-medium hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onResetData('operational');
                  setShowOperationalResetConfirm(false);
                }}
                className="flex-1 px-4 py-2 rounded-xl bg-orange-500 text-white font-medium hover:bg-orange-600 transition-colors"
              >
                Reset Operational Data
              </button>
            </div>
          </div>
        </div>
      )}

      {showGenerateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-stone-800">Generate Sample Data?</h3>
            </div>
            <p className="text-stone-600 mb-6">
              This will create sample venues, vendors, services, packages, clients, and events for testing purposes. 
              Existing data will not be affected.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowGenerateConfirm(false)}
                className="flex-1 px-4 py-2 rounded-xl border border-stone-200 text-stone-600 font-medium hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onGenerateDummyData}
                className="flex-1 px-4 py-2 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors"
              >
                Generate Data
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-stone-800">Import Data?</h3>
            </div>
            <p className="text-stone-600 mb-4">
              You are about to import data from <strong>{selectedFile?.name}</strong>
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-stone-700 mb-2">Import Strategy</label>
              <select
                value={importStrategy}
                onChange={(e) => setImportStrategy(e.target.value as 'replace' | 'merge' | 'skip')}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              >
                <option value="skip">Skip Existing (Safe) - Skip records that already exist</option>
                <option value="merge">Merge - Update existing records, add new ones</option>
                <option value="replace">Replace All (Dangerous) - Clear all data and import</option>
              </select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Importing large datasets may take a few minutes. Do not close the browser.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowImportConfirm(false)}
                className="flex-1 px-4 py-2 rounded-xl border border-stone-200 text-stone-600 font-medium hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                className="flex-1 px-4 py-2 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors"
              >
                Import Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataManagementSection;
